import { ctxUser, UserContext } from '@/contexts/UserContext';
import { useContext } from 'react';

function createLocalStorageKey(preferencesKey: string, userCtx: UserContext | undefined): string | undefined {
  if (!userCtx) {
    return undefined;
  }
  const userNamespace = userCtx.user.isTokenUser ? '_token_' : userCtx.user.name;
  return `${preferencesKey}::${userNamespace}`;
}

function getPreferences(localStorageKey: string): any | undefined {
  return localStorage.getItem(localStorageKey);
}

function parseAsObject(localStorageKey: string, preferencesValue: any): any | undefined {
  try {
    return JSON.parse(preferencesValue);
  } catch (_e) {
    localStorage.removeItem(localStorageKey);
    return undefined;
  }
}

function setPreferences(localStorageKey: string, preferencesValue: any) {
  localStorage.setItem(localStorageKey, preferencesValue);
}

export function usePreferencesStorage() {
  const userCtx = useContext(ctxUser);

  return {
    get: (preferencesKey: string) => {
      const localStorageKey = createLocalStorageKey(preferencesKey, userCtx);
      if (!localStorageKey) {
        return undefined;
      }
      return getPreferences(localStorageKey);
    },
    getOrDefault: (preferencesKey: string, defaultValue: any) => {
      const localStorageKey = createLocalStorageKey(preferencesKey, userCtx);
      if (!localStorageKey) {
        return undefined;
      }
      return getPreferences(localStorageKey) || defaultValue;
    },
    getObject: (preferencesKey: string) => {
      const localStorageKey = createLocalStorageKey(preferencesKey, userCtx);
      if (!localStorageKey) {
        return undefined;
      }
      const preferencesValue = getPreferences(localStorageKey);
      if (!preferencesValue) {
        return undefined;
      }
      return parseAsObject(localStorageKey, preferencesValue);
    },
    getObjectOrDefault: (preferencesKey: string, defaultValue: any) => {
      const localStorageKey = createLocalStorageKey(preferencesKey, userCtx);
      if (!localStorageKey) {
        return defaultValue;
      }
      const preferencesValue = getPreferences(localStorageKey);
      if (!preferencesValue) {
        return defaultValue;
      }
      return parseAsObject(localStorageKey, preferencesValue) || defaultValue;
    },
    set: (preferencesKey: string, preferencesValue: any) => {
      const localStorageKey = createLocalStorageKey(preferencesKey, userCtx);
      if (!localStorageKey) {
        return;
      }
      setPreferences(localStorageKey, preferencesValue);
    },
    setObject: (preferencesKey: string, preferencesValue: any) => {
      const localStorageKey = createLocalStorageKey(preferencesKey, userCtx);
      if (!localStorageKey) {
        return;
      }
      const preferencesAsString = JSON.stringify(preferencesValue);
      setPreferences(localStorageKey, preferencesAsString);
    },
  };
}
